import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import TableWrapper from "../../../core/BaseComponents/TableWrapper/TableWrapper";
import { Card, CircularProgress, Switch, Tooltip, Select, MenuItem } from "@mui/material";
import { callAPI } from "../../../../axios";
import BasicModal from "../../../core/BaseComponents/Modal";
import Custom_Paingation from "../../../core/BaseComponents/Custom_Paingation";
import { updateMasterHotelActions } from "../../../../store/actions/UpdateMasterHotel/UpdateMasterHotelActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { debounce } from 'lodash';
import { isValidRole, HOTEL_DASHBOARD_VIEW_TYPE, activeHotelFilterClass, formatHotelDisplayName } from "../../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import IndiaMapWithSTB from "./IndiaMap/IndiaMapWithSTB";

/**
 * Dashboard summary tabs. Hotel list uses `viewType` on hotel/search (`custom` = HMS, `hotelApp` = third party).
 * Map uses `filterType` + `hotelType` from `/indiaMap` (see geoUtils).
 */
const HOTELS_DASHBOARD_SUMMARY_TABS = [
  {
    id: 'total',
    kind: 'click',
    mapFilterType: 'all',
    viewType: HOTEL_DASHBOARD_VIEW_TYPE.TOTAL,
    hotelCountKey: 'totalhotel',
    cdsnCountKey: 'totalCdsn',
    cdsnStripTitle: "Total CDSN's",
    hotelCardTitle: 'Total Hotels',
  },
  {
    id: 'hms',
    kind: 'click',
    mapFilterType: 'HMS',
    viewType: HOTEL_DASHBOARD_VIEW_TYPE.HMS,
    hotelCountKey: 'hmsHotel',
    cdsnCountKey: 'hmsCdsn',
    cdsnStripTitle: "HMS CDSN's",
    hotelCardTitle: 'HMS Hotels',
  },
  {
    id: 'thirdParty',
    kind: 'thirdPartyBrandSelect',
    mapFilterType: 'THIRD_PARTY',
    viewType: HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY,
    hotelCountKey: 'thirdPartyHotel',
    cdsnCountKey: 'thirdPartyCdsn',
    cdsnStripTitle: "Third Party CDSN's",
    hotelCardTitle: 'Third Party Hotels',
  },
];

const THIRD_PARTY_ALL_BRANDS = 'All';

const buildThirdPartyBrandOptions = (
  hotelCountMap = {},
  cdsnCountMap = {},
  totalHotelCount = 0,
  totalCdsnCount = 0,
  brandDisplayNames = {}
) => {
  const keys = new Set([
    ...Object.keys(hotelCountMap || {}),
    ...Object.keys(cdsnCountMap || {}),
  ]);
  const brandOptions = [...keys].map((brand, index) => ({
    id: `${brand}-${index}`,
    brand,
    displayName: brandDisplayNames?.[brand] || brand,
    count: Number(hotelCountMap[brand]) || 0,
    cdsnCount: Number(cdsnCountMap[brand]) || 0,
    isAll: false,
  }));

  return [
    {
      id: 'all-third-party-brands',
      brand: THIRD_PARTY_ALL_BRANDS,
      displayName: THIRD_PARTY_ALL_BRANDS,
      count: Number(totalHotelCount) || 0,
      cdsnCount: Number(totalCdsnCount) || 0,
      isAll: true,
    },
    ...brandOptions,
  ];
};

function HotelsDashboard() {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const [hotelDashboardCount, setHotelDashboardCount] = useState({})
  const [hotelList, setHotelList] = useState([]);
  const [cdnsShowPopUp, setCdnsShowPopUp] = useState(false);
  const [rowData, setRowData] = useState(false);
  const [loader, setLoader] = useState(false);
  const [hotelTypeFilter, setHotelTypeFilter] = useState(HOTEL_DASHBOARD_VIEW_TYPE.TOTAL)
  const [filterType, setFilterType] = useState("all")
  const [brandFilter, setBrandFilter] = useState("")
  const [thirdPartyHotelData, setThirdPartyHotelData] = useState([])
  const [selectedHotel, setSelectedHotel] = useState(null)
  const [mapChainIds, setMapChainIds] = useState(null)

  const recordsPerPage = 15;

  const { chainId } = useSelector((state) => state.chainIdReducer);

  const [selectedPage, setSelectedPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [paginationError, setPaginationError] = useState("")
  const [inputPageValue, setInputPageValue] = useState(1)


  const [searchText, setSearchText] = useState("");

  let validPermissionClassName = isValidRole("HOTEL_CONFIGURATION_MODIFY")
    ? ""
    : "btn-disable-permission";

  const renderCDSNS = (row) => {
    const [item, ...items] = row?.cdsns;
    return row?.cdsns?.length > 0 ? <span className="cursor-pointer" onClick={() => { setCdnsShowPopUp(true); setRowData(row) }}>{item} {items.length > 0 && (
      <><span>...</span><span className="ms-2 cdsn-count">+{items?.length}</span></>
    )}</span> : (
      <div>N.A</div>
    )
  }

  const applyHotelsDashboardSummaryTab = useCallback((tab) => {
    setHotelTypeFilter(tab.viewType);
    setFilterType(tab.mapFilterType);
    setSelectedHotel(null);
    setBrandFilter("");
    setMapChainIds(null);
  }, []);

  const handleMapRegionChange = useCallback((nextRegion) => {
    setMapChainIds(nextRegion?.chainIds?.length ? nextRegion.chainIds : null);
  }, []);

  const totalSummaryTab = HOTELS_DASHBOARD_SUMMARY_TABS.find(
    (t) => t.viewType === HOTEL_DASHBOARD_VIEW_TYPE.TOTAL
  );
  const thirdPartySummaryTab = HOTELS_DASHBOARD_SUMMARY_TABS.find(
    (t) => t.kind === 'thirdPartyBrandSelect'
  );

  const triggerTotalHotel = () => {
    if (totalSummaryTab) applyHotelsDashboardSummaryTab(totalSummaryTab);
  };

  const triggerThirdPartyHotel = (hotelBrand) => {
    setHotelTypeFilter(HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY);
    setFilterType(thirdPartySummaryTab?.mapFilterType ?? "THIRD_PARTY");
    setBrandFilter(hotelBrand);
    setMapChainIds(null);
  };

  /** Third-party hotels with no brand filter — all brands. */
  const triggerAllBrandHotels = () => {
    setHotelTypeFilter(HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY);
    setFilterType(thirdPartySummaryTab?.mapFilterType ?? "THIRD_PARTY");
    setBrandFilter("");
    setSelectedHotel(
      thirdPartyHotelData.find((hotel) => hotel.isAll) ?? null
    );
  };

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false
    }
    else {
      if (hotelDashboardCount?.tickerMessages?.length) {
        hotelDashboardCount?.tickerMessages?.map((data) => {
          return (
            toast(data, {
              closeButton: true,
              autoClose: 6000,
              position: 'top-right',
              type: 'error'
            })
          )
        })
      }
    }
  }, [hotelDashboardCount])

  const columns = [
    {
      field: "hotelName",
      headerName: "Hotel Name",
      flex: 1,
      row: true,
      renderCell: (row) => formatHotelDisplayName(row),
    },
    { field: "contactNo", headerName: "Phone", flex: 1 },
    // { field: "email", headerName: "Email", flex: 1 },
    // { field: "entepriseChainId", headerName: "Chain ID", flex: 1 },
    {
      field: "CDSN", headerName: "Linked CDSNs", flex: 1,
      row: true, renderCell: renderCDSNS,
      sortDirection: false,
      disableSortIcon: true
    },
    {
      field: "logoUrl", headerName: "Logo", flex: 1,
      row: true, renderCell: (row) => row?.logoUrl === null ? "NA" : <div className="uploaded-image">
        <img src={row?.logoUrl} alt={row?.logoUrl} />
      </div>,
      sortDirection: false,
      disableSortIcon: true
    },
    // {
    //   field: "autoMode", headerName: "Auto Mode Status", flex: 1,
    //   row: true, renderCell: (row) => <Switch
    //       checked={row?.autoMode}
    //       disabled
    //       inputProps={{ "aria-label": "controlled" }}
    //       className={`${row?.autoMode ? 'switchuser-active' : 'switchuser-inactive'} edit-icon-disable`}
    //     />,
    //   sortDirection: false,
    //   disableSortIcon: true
    // },
  ];

  useEffect(() => {
    const apiPath = `/getHoteldashBoard`;
    setLoader(true);
    callAPI(apiPath, "GET")
      .then((response) => {
        setHotelDashboardCount(response)
        setThirdPartyHotelData(
          buildThirdPartyBrandOptions(
            response?.brandWiseHotelCount,
            response?.brandWiseCdsnCount,
            response?.thirdPartyHotel,
            response?.thirdPartyCdsn,
            response?.brandDisplayNames
          )
        )
        setLoader(false);
      })
      .catch((err) => { console.warn(err.message); setLoader(false); });
  }, [])

  const hotelListApi = () => {
    const apiPath = `hotel/search?pageNo=${selectedPage}&recordsPerPage=${recordsPerPage}`;
    setLoader(true);
    callAPI(apiPath, "POST", { hotelName: searchText, viewType: hotelTypeFilter, testHotel: false, demoHotel: false, isDashboardPage: true , hotelBrand: brandFilter, chainIds: mapChainIds})
      .then((response) => {
        setHotelList(response?.hotelDetails?.map((data) => ({ ...data, sid: data.id })));
        setTotalPageCount(response?.totalPages ? response?.totalPages : 1)
        setPaginationError("")
        dispatch(updateMasterHotelActions())
        setLoader(false);
      })
      .catch((err) => { console.warn(err.message); setLoader(false); });
  };

  useEffect(() => {
    hotelListApi();
  }, [selectedPage, chainId, searchText, hotelTypeFilter, filterType, brandFilter, mapChainIds]);

  useEffect(() => {
    setInputPageValue(1)
    setPaginationError("")
    setSelectedPage(1);
  }, [searchText, hotelTypeFilter, brandFilter, mapChainIds]);

  const popupCloseHandler = () => {
    setCdnsShowPopUp(false);
  };

  const DownloadTextFile = (headings, fileName) => {
    const columnHeaders = 'CDSN\n';
    const textArray = headings?.map((category) => {
      const categoryText = `${category?.header}\n`; // Section heading
      const categoryBody = category?.body?.map((item) =>
        `${item?.length ? item : 'NA'}`)?.join('\n'); // Data rows with SID and CDSN
      return `${categoryText}${columnHeaders}${categoryBody || 'No data available'}\n`; // Include the column headers before the data
    });

    const fileText = textArray?.join('\n');
    const blob = new Blob([fileText], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downLoadFile = () => {
    const fileName = `${rowData?.hotelName}_${rowData?.hotelChainId}_CDSNs.csv`;
    const headings = [
      { header: `List of "${rowData?.hotelName}" Linked CDSN's`, body: rowData?.cdsns?.length ? rowData?.cdsns : [{ sid: 'NA', cdsn: ['NA'] }] }
    ];
    DownloadTextFile(headings, fileName);
  };


  //Pagination Handle changer
  const onPageChange = (event, value) => {
    setInputPageValue(value)
    setPaginationError("")
    setSelectedPage(value);
  };

  const handleSearch = (search) => {
    setSearchText(search)
  };

  const delayedSearch = useCallback(
    debounce((searchText) => handleSearch(searchText), 600),
    []
  );

  const selectedThirdPartyHotelCount =
    selectedHotel != null
      ? selectedHotel.count
      : (thirdPartySummaryTab?.hotelCountKey != null
          ? (hotelDashboardCount?.[thirdPartySummaryTab.hotelCountKey] ?? 0)
          : 0);

  const mapBrandMatchAliases = useMemo(() => {
    if (!brandFilter) {
      return [];
    }
    const aliases = [];
    if (selectedHotel?.displayName) {
      aliases.push(selectedHotel.displayName);
    }
    const mappedDisplay = hotelDashboardCount?.brandDisplayNames?.[brandFilter];
    if (mappedDisplay) {
      aliases.push(mappedDisplay);
    }
    return aliases;
  }, [brandFilter, selectedHotel, hotelDashboardCount]);

  return (
    <>
      <p className="mb-1 ms-1"><b>Hotels Dashboard</b></p>
      <div className="hotel-dashboard">
        {HOTELS_DASHBOARD_SUMMARY_TABS.map((tab) => (
          <Card key={tab.id} className="dashboard-non-clickable ">
            <p>{tab.cdsnStripTitle}</p>
            <h6>{hotelDashboardCount?.[tab.cdsnCountKey] ? hotelDashboardCount[tab.cdsnCountKey] : 0}</h6>
          </Card>
        ))}
      </div>
      <div className="hotel-dashboard">
        {HOTELS_DASHBOARD_SUMMARY_TABS.filter((tab) => tab.kind === 'click').map((tab) => (
          <Card
            key={tab.id}
            className={`cursor-pointer ${activeHotelFilterClass(hotelTypeFilter, tab.viewType)}`}
            onClick={() => applyHotelsDashboardSummaryTab(tab)}
          >
            <p>{tab.hotelCardTitle}</p>
            <h6>{hotelDashboardCount?.[tab.hotelCountKey] ? hotelDashboardCount[tab.hotelCountKey] : 0}</h6>
          </Card>
        ))}
        <div style={{ flex: 1.07, display: 'flex', alignItems: 'center', gap: '10px', padding: '0px' }}>
          <Select
            id="third-party-hotel-select"
            name="third-party-hotel-select"
            value={selectedHotel ? selectedHotel.brand : ""}
            onChange={(e) => {
              const value = e.target.value;
              if (selectedHotel?.brand === value) {
                triggerTotalHotel();
                return;
              }
              const selectedHotelData = thirdPartyHotelData.find(hotel => hotel.brand === value);
              setSelectedHotel(selectedHotelData || null);
              if (selectedHotelData?.isAll) {
                setHotelTypeFilter(HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY);
                setFilterType(thirdPartySummaryTab?.mapFilterType ?? "THIRD_PARTY");
                setBrandFilter("");
              } else if (selectedHotelData) {
                triggerThirdPartyHotel(selectedHotelData.brand);
              } else {
                triggerTotalHotel();
              }
            }}
            inputProps={{ id: 'third-party-hotel-input' }}
            displayEmpty
            className={`cursor-pointer ${activeHotelFilterClass(hotelTypeFilter, HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY)}`}
            sx={{
              flex: 0.8,
              height: '88px',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '13px',
              boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: 'rgba(0, 0, 0, 0.23)'
              },
              '& .MuiSelect-select': {
                padding: '16px 25px !important',
                paddingLeft: '0px !important',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                color: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? 'white' : 'inherit'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.activeHotelFilter': {
                backgroundColor: '#de61b4',
                color: 'white',
                borderColor: '#de61b4',
                '& .MuiSelect-select': {
                  color: 'white'
                },
                '&:hover': {
                  backgroundColor: '#de61b4',
                  opacity: 0.9
                }
              },
              '& .MuiSvgIcon-root': {
                color: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? 'white' : 'rgba(0, 0, 0, 0.54)'
              }
            }}
            renderValue={() => (
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '0px' }}>
                <div style={{ fontWeight: 'normal', fontSize: '14px', padding: '0px', color: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? 'white' : 'rgba(0, 0, 0, 0.87)' }}>
                  {thirdPartySummaryTab?.hotelCardTitle ?? 'Third Party Hotels'}
                </div>
                <p style={{
                  marginTop: '2px',
                  padding: '0px',
                  fontSize: '12px',
                  color: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? 'rgba(255, 255, 255, 0.9)' : '#666'
                }}>
                  {selectedHotel ? (selectedHotel.displayName || selectedHotel.brand) : "Select a brand"}
                </p>
              </div>
            )}
          >
            <MenuItem value="" disabled>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '0px' }}>
                <div style={{ fontWeight: 'normal', fontSize: '14px', padding: '0px', color: 'rgba(0, 0, 0, 0.87)' }}>
                  {thirdPartySummaryTab?.hotelCardTitle ?? 'Third Party Hotels'}
                </div>
                <p style={{
                  marginTop: '2px',
                  padding: '0px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Select a brand
                </p>
              </div>
            </MenuItem>
            {thirdPartyHotelData.map((hotel) => (
              <MenuItem
                key={hotel.id}
                value={hotel.brand}
                onClick={(e) => {
                  if (selectedHotel?.brand === hotel.brand) {
                    e.stopPropagation();
                    triggerTotalHotel();
                  }
                }}
              >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ fontWeight: 'normal', fontSize: '14px' }}>
                    {hotel.displayName || hotel.brand}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: 1.35,
                  }}>
                    <span>count: {hotel.count}</span>
                    <span>cdsn: {hotel.cdsnCount}</span>
                  </div>
                </div>
              </MenuItem>
            ))}
          </Select>
          <Card
            className={`${activeHotelFilterClass(hotelTypeFilter, HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY)}`}
            // onClick={triggerAllBrandHotels}
            style={{
              flex: 0.2,
              height: '88px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '13px',
              padding: '4px',
              backgroundColor: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? '#de61b4' : 'white',
              color: hotelTypeFilter === HOTEL_DASHBOARD_VIEW_TYPE.THIRD_PARTY ? 'white' : 'black',
              boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
            }}
          >
            <span style={{ fontWeight: 'bold', textAlign: 'center', fontSize: '20px', fontFamily: '"Roboto Bold"' }}>
              {selectedThirdPartyHotelCount}
            </span>
          </Card>
        </div>
      </div>

      <div className="col-12 row hotels-dashboard-equal-height-panels">
        <div className="col-6">
          <IndiaMapWithSTB
            filterType={filterType}
            brandFilter={brandFilter}
            brandMatchAliases={mapBrandMatchAliases}
            onRegionChange={handleMapRegionChange}
          />
        </div>
        <div className="col-6 px-0">
          <div className='row config-hotel'>
            <div className='left-column pe-0'>
              {loader && <div className="circular-progress"><CircularProgress /></div>}
              <div className="hotel-list">
                <div className="flex-items-between mb-3">
                  <p className="mb-0"><b>Hotels List</b></p>
                  <div className="d-flex">
                    {isValidRole("HOTEL_CONFIGURATION_MODIFY") && <button onClick={() => navigate("/hms/ConfigurationNewHotel")} className={`create-btn mx-1 ${validPermissionClassName}`}>Add Hotel</button>}
                    <div className="form-group-hotel mb-0">
                      <div className={`floating-label`}>
                        <input
                          id="search"
                          placeholder=""
                          className="form-control"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) {
                              setSelectedPage(1);
                              setInputPageValue(1);
                            }
                            delayedSearch(value)
                          }}
                        />
                        <label htmlFor="guestName">Search Hotel</label>
                      </div>
                    </div>
                  </div>
                </div>
                <TableWrapper
                  data={hotelList}
                  columns={columns}
                  module="hotel-config"
                  noPagination={true}
                />
                <Custom_Paingation
                  selectedPage={selectedPage}
                  totalPageCount={totalPageCount}
                  paginationError={paginationError}
                  inputPageValue={inputPageValue}
                  setSelectedPage={setSelectedPage}
                  setPaginationError={setPaginationError}
                  setInputPageValue={setInputPageValue}
                  onPageChange={onPageChange}
                />
              </div>
            </div>
            {cdnsShowPopUp && <BasicModal
              popupOpen={cdnsShowPopUp}
              bodyMessage={
                <div className="text-start">
                  <p className="px-3 pt-3">List of "<b>{rowData?.hotelName}</b>" Linked CDSNs</p>
                  <div className="linked-sid-cdsns">
                    <div className="sid-cdsn-list">
                      <div className="row sid-cdsn-list-header">
                        <h6 className="text-start">CDSN's</h6>
                      </div>
                      <div className="my-3 px-3">
                        {rowData?.cdsns?.map((items, index) =>
                          <span className="mb-1">{index ? ', ' : ''} {items}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              }
              customcss={true}
              footer={
                {
                  primaryButton: {
                    buttonName: "Cancel",
                    action: popupCloseHandler,
                    buttonPrimaryCustomCss: 'cancel-btn_new'
                  }
                  ,
                  secondaryButton: {
                    buttonName: "Download",
                    action: downLoadFile
                  }
                }
              }
            />}
          </div>
        </div>
      </div>

    </>
  )
}

export default HotelsDashboard;